import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { TokenCard } from '@pkg/components';
import { EmptyGroup, GroupHeaderHome } from '@pkg/components/Sections/Groups';
import { useAppSelector, useIsCurrentUser, useMedia } from '@pkg/hooks';

import { CollectionChainType } from '@app/client';
import { AssetsImageCacheMaxWidth } from '@app/client/common/structs/assets/assets';
import { type SocialGroupTypeOverride } from '@app/client/common/structs/groups/groups';
import { getMashedUrl } from '@app/helpers';
import { GROUP_BASE } from '@app/routes/paths/groups';

import {
    ArrowRight,
    GroupWrapper,
    NftWrapper,
    NftWrapperGrid,
    SeeMoreGroupCard,
    SeeMoreGroupCardHeading,
    SeeMoreGroupCardSubheading,
} from './styles';

type Props = Pick<
    SocialGroupTypeOverride,
    'id' | 'name' | 'description' | 'header_image' | 'privacy' | 'members' | 'following' | 'owner' | 'items' | 'slug'
> & {
    followerCount?: number;
    membersCount?: number;
    followerCountWeek?: number;
    hasMembership?: boolean;
    itemTotal?: number;
};

export const usePodsNumberOfItems = () => {
    const theme = useTheme();

    const showFourItems = useMedia(`(max-width: 1680px)`);
    const showThreeItems = useMedia(`(max-width: 1431px)`);
    const isMobile = useMedia(`(max-width: ${theme.breakpoints.mobile})`);

    return useMemo(() => {
        if (showThreeItems && !isMobile) return 3;
        if (showFourItems && !isMobile) return 4;
        return 5;
    }, [showFourItems, showThreeItems, isMobile]);
};

export const GroupItem = ({
    id,
    name,
    description,
    header_image,
    privacy,
    members,
    following,
    followerCount,
    membersCount,
    owner,
    slug,
    items,
    followerCountWeek,
    hasMembership,
    itemTotal,
}: Props) => {
    const isOwner = useIsCurrentUser(owner);
    const { showEvents } = useAppSelector((state) => state.featureFlags.features);
    const { t } = useTranslation(['groups']);

    const showSmallCards = useMedia(`(max-width: 926px)`);
    const showThisManyItems = usePodsNumberOfItems();

    const cloudinaryImageUrl = getMashedUrl({
        width: AssetsImageCacheMaxWidth.POD_NFT_CARD,
        url: header_image,
    });

    const remainingAssets = itemTotal ? itemTotal - 4 : null;
    const hasMultiple = remainingAssets && remainingAssets > 1;

    return (
        <GroupWrapper>
            <GroupHeaderHome
                id={id}
                href={`${GROUP_BASE}/${slug}`}
                name={name}
                description={description}
                image={cloudinaryImageUrl}
                privacy={privacy}
                members={members}
                isFollowing={following}
                isOwner={isOwner}
                membersCount={membersCount}
                followerCount={followerCount}
                followerCountWeek={followerCountWeek}
                hasMembership={hasMembership}
            />

            {items.length ? (
                <NftWrapper $showEvents={showEvents}>
                    <NftWrapperGrid $showThisManyItems={showThisManyItems} $showSmallCards={showSmallCards}>
                        {items.map((item, index) => {
                            if (index > showThisManyItems - 1) return null;
                            return (
                                <TokenCard
                                    isCollectionTile
                                    chain={CollectionChainType.Ethereum}
                                    key={item.item.id}
                                    groupItem={item}
                                    cardSize={showSmallCards ? 'small' : undefined}
                                />
                            );
                        })}
                        {items.length > 0 && (
                            <SeeMoreGroupCard to={`${GROUP_BASE}/${slug}`} $showEvents={showEvents}>
                                <SeeMoreGroupCardHeading>{t('groups_see_more_heading')}</SeeMoreGroupCardHeading>
                                {remainingAssets && remainingAssets > 0 && (
                                    <SeeMoreGroupCardSubheading>
                                        {hasMultiple
                                            ? t('groups_see_more_subheading_plural', { count: remainingAssets })
                                            : t('groups_see_more_subheading', { count: remainingAssets })}
                                    </SeeMoreGroupCardSubheading>
                                )}
                                <ArrowRight />
                            </SeeMoreGroupCard>
                        )}
                    </NftWrapperGrid>
                </NftWrapper>
            ) : (
                <EmptyGroup />
            )}
        </GroupWrapper>
    );
};
